import { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography } from "@mui/material";
import { transferMilkRecordingMeasurements } from "../../api/index.js";

const labels = { morning: "Πρωί", noon: "Μεσημέρι", evening: "Βράδυ" };

export default function MilkMeasurementTransfer({ recordingId, onClose, onTransferred }) {
    const [options, setOptions] = useState(null);
    const [source, setSource] = useState("");
    const [target, setTarget] = useState("");
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const submitting = useRef(false);

    useEffect(() => {
        let active = true;
        transferMilkRecordingMeasurements(recordingId).then((response) => {
            if (!active) return;
            if (!response.success) { setError(response.message); return; }
            setOptions(response.transferOptions);
            const sources = response.transferOptions.sources.filter((entry) => entry.animals > 0);
            if (sources.length === 1) setSource(sources[0].type);
            const targets = response.transferOptions.targets.filter((entry) => entry.time);
            if (targets.length === 1) setTarget(targets[0].type);
        }).catch(() => { if (active) setError("Δεν ήταν δυνατή η φόρτωση των μετρήσεων. Κλείστε και ανοίξτε ξανά τη μεταφορά."); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [recordingId]);

    useEffect(() => {
        if (!source || !target) return;
        let active = true;
        setPreview(null);
        setError("");
        setLoading(true);
        transferMilkRecordingMeasurements(recordingId, { source, target }).then((response) => {
            if (!active) return;
            if (response.success && response.preview) setPreview(response);
            else setError(response.message || "Δεν είναι δυνατή η μεταφορά.");
        }).catch(() => { if (active) setError("Ο έλεγχος της μεταφοράς απέτυχε. Δοκιμάστε ξανά."); })
            .finally(() => { if (active) setLoading(false); });
        return () => { active = false; };
    }, [recordingId, source, target]);

    const confirm = async () => {
        if (submitting.current || !preview) return;
        submitting.current = true;
        setSaving(true);
        setError("");
        try {
            const response = await transferMilkRecordingMeasurements(recordingId, {
                source, target, confirm: true, changeVersion: preview.changeVersion,
            });
            if (!response.success) {
                setPreview(null);
                setError(response.message || "Η μεταφορά δεν ολοκληρώθηκε.");
            } else if (response.preview) {
                setPreview(response);
                setError(response.message);
            } else onTransferred(response);
        } catch {
            setPreview(null);
            setError("Δεν ήταν δυνατή η επιβεβαίωση της μεταφοράς. Κλείστε το παράθυρο και ελέγξτε ξανά τις μετρήσεις.");
        } finally {
            submitting.current = false;
            setSaving(false);
        }
    };

    const choose = (setter) => (event) => { setPreview(null); setter(event.target.value); };
    return <Dialog open onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="milk-transfer-title">
        <DialogTitle id="milk-transfer-title">Μεταφορά μετρήσεων σε άλλη άμελξη</DialogTitle>
        <DialogContent>
            <Typography sx={{ mb: 2 }}>
                Αν καταχωρήσατε τις ποσότητες σε λάθος άμελξη, μπορείτε να τις μεταφέρετε όλες μαζί στη σωστή.
                Επιλέξτε από ποια άμελξη θα φύγουν και σε ποια θα μεταφερθούν.
                Προορισμός μπορεί να είναι μόνο άμελξη που δεν είναι επιλεγμένη ως μετρημένη.
            </Typography>
            {options && <Box sx={{ display: "flex", flexDirection: "column", gap: 2, my: 2 }}>
                <TextField select fullWidth label="Από ποια άμελξη" value={source} onChange={choose(setSource)} disabled={saving}>
                    {options.sources.map((entry) => <MenuItem key={entry.type} value={entry.type} disabled={entry.animals === 0}>
                        {labels[entry.type]} — {entry.animals} {entry.animals === 1 ? "ζώο" : "ζώα"}{entry.animals === 0 ? " (χωρίς μετρήσεις)" : ""}
                    </MenuItem>)}
                </TextField>
                <TextField select fullWidth label="Σε ποια άμελξη" value={target} onChange={choose(setTarget)} disabled={saving}>
                    {options.targets.map((entry) => <MenuItem key={entry.type} value={entry.type} disabled={!entry.time}>
                        {labels[entry.type]}{!entry.time ? " — αποθηκεύστε πρώτα την ώρα άμελξης" : ""}
                    </MenuItem>)}
                </TextField>
                {!options.sources.some((entry) => entry.animals > 0) && <Alert severity="info">Δεν υπάρχουν μετρήσεις για μεταφορά.</Alert>}
                {options.targets.length === 0 && <Alert severity="info">Όλες οι αμέλξεις είναι επιλεγμένες ως μετρημένες. Δεν υπάρχει διαθέσιμος προορισμός.</Alert>}
            </Box>}
            {loading && <Box role="status" sx={{ display: "flex", alignItems: "center", gap: 1, my: 2 }}><CircularProgress size={20} />Έλεγχος μετρήσεων…</Box>}
            {error && <Alert severity="warning" sx={{ my: 2 }}>{error}</Alert>}
            {preview && <Alert severity="info" sx={{ mt: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>Μεταφορά από {labels[source]} σε {labels[target]}</Typography>
                <Typography>Θα μεταφερθούν οι μετρήσεις {preview.transferredAnimals} {preview.transferredAnimals === 1 ? "ζώου" : "ζώων"}. Οι ποσότητες γάλακτος θα παραμείνουν ίδιες.</Typography>
                <Typography sx={{ mt: 1 }}>Η άμελξη «{labels[target]}» θα επιλεγεί ως μετρημένη και η άμελξη «{labels[source]}» θα αποεπιλεγεί. Οι ώρες αρμέγματος δεν θα αλλάξουν.</Typography>
                <Typography sx={{ mt: 1, fontWeight: 700 }}>Μετά τη μεταφορά, πρέπει να κάνετε ξανά τον υπολογισμό της πραγματικής και τυπικής γαλακτοπαραγωγής. Η επανεκτέλεση είναι δική σας ευθύνη.</Typography>
            </Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2, flexWrap: "wrap", gap: 1 }}>
            <Button onClick={onClose} disabled={saving}>Ακύρωση</Button>
            <Button variant="contained" onClick={confirm} disabled={!preview || loading || saving}>
                {saving ? "Γίνεται μεταφορά…" : preview ? `Μεταφορά ${preview.transferredAnimals} ${preview.transferredAnimals === 1 ? "μέτρησης" : "μετρήσεων"}` : "Μεταφορά μετρήσεων"}
            </Button>
        </DialogActions>
    </Dialog>;
}
